import { isLeaveRecordVisible } from '../utils/leaveVisibility';
import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  ApplyLeaveInput,
  LeaveApplicationDetails,
  LeaveApplicationListItem,
  LeaveBalance,
  LeaveDashboardData,
  LeaveDurationOption,
  LeaveDurationRule,
  LeaveHistoryFilters,
  LeaveHistoryResult,
  LeaveSessionOption,
} from '../types/leave';
import {
  createDefaultLeaveFilters,
  normalizeLeaveFilters,
} from '../utils/leaveFilters';
import { calendarDateFromApi } from '../utils/dateTime';
import {
  groupLeaveDurationOptions,
  normalizeSessionCode,
} from '../utils/leaveSession';
import { getLeaveStatusLabel } from '../utils/leaveStatus';
import { resolveRequestCanCancel } from '../utils/requestCancelWindow';
import { computeSignedLeaveAvailable } from '../utils/leaveBalance';
import { parseMonthlyLopSummary } from '../utils/lopSummary';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function toCamelKey(key: string): string {
  return key.replace(/_([a-z0-9])/gi, (_, ch: string) => ch.toUpperCase());
}

/** Maps MySQL/SP snake_case columns (session_type, session_type_name) to API keys. */
function camelizeRow(row: ObjectValue): ObjectValue {
  const flattened: ObjectValue = { ...row };
  for (const [key, value] of Object.entries(row)) {
    flattened[toCamelKey(key)] = value;
  }
  return flattened;
}

function dateOnly(value: unknown): string | null {
  return calendarDateFromApi(value);
}

function reasonSummary(value: unknown): string | null {
  const text = textOrNull(value);
  if (!text) return null;
  return text.length > 80 ? `${text.slice(0, 77)}...` : text;
}

function booleanOrUndefined(value: unknown): boolean | undefined {
  if (value === true || value === 1 || value === '1' || value === 'true') {
    return true;
  }
  if (value === false || value === 0 || value === '0' || value === 'false') {
    return false;
  }
  return undefined;
}

function isLeaveCancellable(statusCode: string | null, row: ObjectValue): boolean {
  return resolveRequestCanCancel({
    statusCode,
    canCancelFromApi: booleanOrUndefined(
      row.canCancel ?? row.can_cancel ?? row.allowCancel ?? row.allow_cancel,
    ),
    fromDate: dateOnly(row.leaveFromDate ?? row.fromDate ?? row.startDate),
    toDate: dateOnly(row.leaveToDate ?? row.toDate ?? row.endDate),
  });
}

function normalizeBalance(raw: ObjectValue): LeaveBalance {
  const row = camelizeRow(raw);
  const isLossOfPay = flagOrNull(row.isLossOfPay);
  const available = computeSignedLeaveAvailable({
    openingBalance: row.openingBalance ?? row.openingBalanceDays,
    creditedDays: row.accrued ?? row.accruedDays ?? row.creditedDays ?? row.credited,
    adjustedDays: row.adjustedDays ?? row.adjusted,
    debitedDays:
      row.used ??
      row.usedDays ??
      row.debitedDays ??
      row.consumed ??
      row.availed,
    pendingLeaveDays: row.pending ?? row.pendingDays ?? row.pendingLeaveDays,
    availableBalance:
      row.availableBalance ?? row.available ?? row.availableDays ?? row.balanceDays ?? row.balance,
  });
  return {
    leaveTypeId: numberOrNull(row.leaveTypeId),
    leavePolicyId: numberOrNull(row.leavePolicyId),
    leaveTypeCode: textOrNull(row.leaveTypeCode),
    leaveTypeName:
      textOrNull(row.leaveTypeName) ??
      textOrNull(row.leaveName) ??
      textOrNull(row.leaveTypeCode) ??
      'Leave type',
    openingBalance: numberOrNull(
      row.openingBalance ?? row.openingBalanceDays,
    ),
    accrued: numberOrNull(
      row.accrued ?? row.accruedDays ?? row.creditedDays ?? row.credited,
    ),
    used: numberOrNull(
      row.used ??
        row.usedDays ??
        row.debitedDays ??
        row.consumed ??
        row.availed,
    ),
    pending: numberOrNull(
      row.pending ?? row.pendingDays ?? row.pendingLeaveDays,
    ),
    available: isLossOfPay ? null : available,
    isLossOfPay,
    requiresBalance: isLossOfPay ? false : booleanOrUndefined(row.requiresBalance),
    daysTaken: numberOrNull(row.daysTaken) ?? 0,
    leaveYear: numberOrNull(row.leaveYear),
    lopDays: Math.max(0, numberOrNull(row.lopDays) ?? 0),
    annualEntitlement: numberOrNull(row.annualEntitlement),
    monthlyAccrual: numberOrNull(row.monthlyAccrual),
    unit: textOrNull(row.unit ?? row.leaveUnit) ?? 'DAYS',
    allowFullDay:
      row.allowFullDay == null && row.allow_full_day == null
        ? null
        : Boolean(row.allowFullDay ?? row.allow_full_day),
    allowHalfDay:
      row.allowHalfDay == null && row.allow_half_day == null
        ? null
        : Boolean(row.allowHalfDay ?? row.allow_half_day),
    receivesCompensatoryCredit:
      row.receivesCompensatoryCredit == null &&
      row.receives_compensatory_credit == null
        ? null
        : Boolean(
            row.receivesCompensatoryCredit ?? row.receives_compensatory_credit,
          ),
    carryForwardAllowed:
      row.carryForwardAllowed == null && row.carry_forward_allowed == null
        ? null
        : flagOrNull(row.carryForwardAllowed ?? row.carry_forward_allowed),
    monthAvailableBalance: numberOrNull(
      row.monthAvailableBalance ?? row.month_available_balance,
    ),
    accrualBasis: textOrNull(row.accrualBasis ?? row.accrual_basis),
  };
}

function normalizeLeaveRule(raw: ObjectValue): LeaveDurationRule | null {
  if (!isLeaveRecordVisible(raw)) return null;
  const row = camelizeRow(raw);
  const leaveTypeId = numberOrNull(row.leaveTypeId);
  const durationCode = normalizeSessionCode(textOrNull(row.durationCode));
  if (leaveTypeId == null || leaveTypeId <= 0 || !durationCode) return null;
  return {
    leaveTypeId,
    leaveTypeName: textOrNull(row.leaveTypeName),
    durationCode,
    durationName: textOrNull(row.durationName),
    durationDays: numberOrNull(row.durationDays),
    isHalfDay: flagOrNull(row.isHalfDay),
    requiresExplicitRule: flagOrNull(row.requiresExplicitRule),
    hasRule: flagOrNull(row.hasRule),
    isAllowed: flagOrNull(row.isAllowed),
    minimumAdvanceDays: Math.max(0, numberOrNull(row.minimumAdvanceDays) ?? 0),
    maximumRequestsPerMonth: numberOrNull(row.maximumRequestsPerMonth),
    serverDate: dateOnly(row.serverDate),
  };
}

function normalizeApplication(raw: ObjectValue): LeaveApplicationListItem | null {
  if (!isLeaveRecordVisible(raw)) return null;
  const row = camelizeRow(raw);
  const leaveApplicationId = numberOrNull(
    row.leaveApplicationId ?? row.applicationId,
  );
  if (leaveApplicationId === null || leaveApplicationId <= 0) return null;

  const statusCode =
    textOrNull(row.statusCode) ?? textOrNull(row.status) ?? null;

  return {
    leaveApplicationId,
    applicationNumber: textOrNull(
      row.applicationNumber ?? row.leaveApplicationNumber,
    ),
    leaveTypeId: numberOrNull(row.leaveTypeId),
    leaveTypeName: textOrNull(row.leaveTypeName ?? row.leaveName),
    leaveFromDate: dateOnly(row.leaveFromDate ?? row.fromDate ?? row.startDate),
    leaveToDate: dateOnly(row.leaveToDate ?? row.toDate ?? row.endDate),
    totalDays: numberOrNull(
      row.totalDays ?? row.leaveDays ?? row.numberOfDays ?? row.durationDays,
    ),
    sessionType:
      textOrNull(
        row.sessionType ?? row.leaveSessionType ?? row.session,
      ) ?? null,
    sessionTypeName:
      textOrNull(
        row.sessionTypeName ?? row.sessionName ?? row.leaveSessionName,
      ) ?? null,
    statusCode,
    statusLabel:
      textOrNull(row.statusLabel ?? row.statusName ?? row.status_label) ??
      getLeaveStatusLabel(statusCode),
    applicationDate: dateOnly(
      row.applicationDate ?? row.appliedOn ?? row.createdAt,
    ),
    reasonSummary: reasonSummary(row.leaveReason ?? row.reason ?? row.remarks),
    canCancel: isLeaveCancellable(statusCode, row),
  };
}

function normalizeDetails(raw: ObjectValue): LeaveApplicationDetails | null {
  const row = camelizeRow(raw);
  const base = normalizeApplication(row);
  if (!base) return null;
  return {
    leaveApplicationId: base.leaveApplicationId,
    applicationNumber: base.applicationNumber,
    leaveType: {
      leaveTypeId: numberOrNull(row.leaveTypeId),
      leaveTypeCode: textOrNull(row.leaveTypeCode),
      leaveTypeName: base.leaveTypeName,
    },
    applicationDate: base.applicationDate,
    leaveFromDate: base.leaveFromDate,
    leaveToDate: base.leaveToDate,
    totalDays: base.totalDays,
    sessionType: base.sessionType,
    sessionTypeName: base.sessionTypeName,
    statusCode: base.statusCode,
    statusLabel: base.statusLabel,
    reason: textOrNull(row.leaveReason ?? row.reason ?? row.remarks),
    emergencyContact: textOrNull(row.emergencyContact),
    handoverNotes: textOrNull(row.handoverNotes ?? row.handover_notes),
    submittedAt: textOrNull(
      row.submittedAt ?? row.appliedAt ?? row.appliedOn ?? row.createdAt,
    ),
    rejectReason: textOrNull(
      row.rejectReason ?? row.rejectionReason ?? row.rejectedReason,
    ),
    cancellationReason: textOrNull(
      row.cancellationReason ??
        row.cancelReason ??
        row.cancelledReason ??
        row.canceledReason ??
        row.cancellation_reason,
    ),
    attachmentName: textOrNull(row.attachmentName ?? row.attachment),
    employee: {
      employeeId: numberOrNull(
        row.employeeId ?? row.requesterEmployeeId ?? row.applicantEmployeeId,
      ),
      displayName: textOrNull(
        row.displayName ??
          row.requesterName ??
          row.employeeName ??
          row.requesterDisplayName,
      ),
      employeeNumber: textOrNull(
        row.employeeNumber ?? row.requesterEmployeeNumber,
      ),
      designationName: textOrNull(
        row.designationName ?? row.requesterDesignationName,
      ),
    },
    canCancel: base.canCancel,
    extendedDetailsAvailable: true,
  };
}

function sortBalances(balances: LeaveBalance[]): LeaveBalance[] {
  return [...balances].sort((left, right) =>
    left.leaveTypeName.localeCompare(right.leaveTypeName),
  );
}

function flagOrNull(value: unknown): boolean {
  if (value === true || value === 1 || value === '1' || value === 'true') {
    return true;
  }
  return false;
}

function normalizeSessionOption(row: ObjectValue): LeaveSessionOption | null {
  const sessionCode = normalizeSessionCode(
    textOrNull(row.sessionCode ?? row.session_code ?? row.sessionType),
  );
  const durationCode = normalizeSessionCode(
    textOrNull(row.durationCode ?? row.duration_code ?? row.durationType),
  );
  const durationDays = numberOrNull(
    row.durationDays ?? row.duration_days ?? row.leaveDays,
  );
  if (!sessionCode || !durationCode || durationDays == null || durationDays <= 0) {
    return null;
  }
  return {
    sessionCode,
    sessionName:
      textOrNull(row.sessionName ?? row.session_name ?? row.sessionTypeName) ??
      sessionCode,
    durationCode,
    durationName:
      textOrNull(
        row.durationName ?? row.duration_name ?? row.durationTypeName,
      ) ?? durationCode,
    durationDays,
    isHalfDay:
      row.isHalfDay == null && row.is_half_day == null
        ? durationDays < 1
        : flagOrNull(row.isHalfDay ?? row.is_half_day),
    requiresExplicitRule: flagOrNull(
      row.requiresExplicitRule ?? row.requires_explicit_rule,
    ),
    workingStartOffset: numberOrNull(
      row.workingStartOffset ?? row.working_start_offset,
    ) ?? 0,
    displayOrder:
      numberOrNull(
        row.sessionDisplayOrder ??
          row.session_display_order ??
          row.displayOrder ??
          row.display_order,
      ) ?? 0,
  };
}

export function durationOptionsFromPayload(
  data: ObjectValue,
): LeaveDurationOption[] {
  const rows = Array.isArray(data.sessionTypes)
    ? data.sessionTypes
    : Array.isArray(data.sessions)
      ? data.sessions
      : Array.isArray(data.items)
        ? data.items
        : [];
  const sessions = rows
    .map(row =>
      row && typeof row === 'object'
        ? normalizeSessionOption(row as ObjectValue)
        : null,
    )
    .filter((item): item is LeaveSessionOption => item !== null);
  return groupLeaveDurationOptions(sessions);
}

export async function getLeaveSessionTypes(): Promise<LeaveDurationOption[]> {
  const response = await apiClient.get<
    Envelope<{ items?: ObjectValue[]; sessionTypes?: ObjectValue[] } | ObjectValue[]>
  >(API_ENDPOINTS.LEAVE.SESSION_TYPES);
  const payload = response.data.data;
  if (Array.isArray(payload)) {
    const sessions = payload
      .map(row =>
        row && typeof row === 'object'
          ? normalizeSessionOption(row as ObjectValue)
          : null,
      )
      .filter((item): item is LeaveSessionOption => item !== null);
    return groupLeaveDurationOptions(sessions);
  }
  return durationOptionsFromPayload((payload ?? {}) as ObjectValue);
}

export async function getLeaveDashboard(
  year = new Date().getFullYear(),
): Promise<LeaveDashboardData> {
  const response = await apiClient.get<
    Envelope<{
      balances?: ObjectValue[];
      statistics?: ObjectValue;
      currentLeave?: ObjectValue | null;
      upcomingLeaves?: ObjectValue[];
      sessionTypes?: ObjectValue[];
      sessions?: ObjectValue[];
      rules?: ObjectValue[];
      lopSummary?: unknown;
    }>
  >(API_ENDPOINTS.LEAVE.DASHBOARD, { params: { year } });

  const data = response.data.data ?? {};
  const balances = Array.isArray(data.balances)
    ? sortBalances(data.balances.filter(isLeaveRecordVisible).map(normalizeBalance))
    : [];
  const statistics = data.statistics ?? {};
  const upcoming = Array.isArray(data.upcomingLeaves)
    ? data.upcomingLeaves
        .map(normalizeApplication)
        .filter((item): item is LeaveApplicationListItem => item !== null)
    : [];

  return {
    balances,
    lopSummary: parseMonthlyLopSummary(data.lopSummary),
    statistics: {
      totalApplications: numberOrNull(
        statistics.totalApplications ?? statistics.totalLeaveApplications,
      ),
      pendingApplications: numberOrNull(
        statistics.pendingApplications ?? statistics.pendingCount,
      ),
    },
    currentLeave: data.currentLeave
      ? normalizeApplication(data.currentLeave as ObjectValue)
      : null,
    upcomingLeaves: upcoming,
    durationOptions: durationOptionsFromPayload(data as ObjectValue),
    rules: Array.isArray(data.rules)
      ? data.rules
          .map(row =>
            row && typeof row === 'object' ? normalizeLeaveRule(row) : null,
          )
          .filter((item): item is LeaveDurationRule => item !== null)
      : [],
  };
}

export async function getLeaveBalances(options: {
  year: number;
  leaveTypeId: number;
}): Promise<LeaveBalance[]> {
  const year = options.year;
  const leaveTypeId = options.leaveTypeId;
  if (!Number.isInteger(year) || year < 2000 || year > 9999) {
    throw Object.assign(new Error('year is invalid'), {
      code: 'VALIDATION_ERROR',
    });
  }
  if (!Number.isInteger(leaveTypeId) || leaveTypeId <= 0) {
    throw Object.assign(new Error('leaveTypeId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.get<Envelope<{ items?: ObjectValue[] }>>(
    API_ENDPOINTS.LEAVE.BALANCES,
    { params: { year, leaveTypeId } },
  );
  const items = Array.isArray(response.data.data?.items)
    ? response.data.data.items
    : [];
  return sortBalances(items.filter(isLeaveRecordVisible).map(normalizeBalance));
}

export async function getLeaveApplications(options: {
  filters?: Partial<LeaveHistoryFilters>;
  page?: number;
  pageSize?: number;
} = {}): Promise<LeaveHistoryResult> {
  const defaults = createDefaultLeaveFilters();
  const normalized = normalizeLeaveFilters({
    fromDate: options.filters?.fromDate ?? defaults.fromDate,
    toDate: options.filters?.toDate ?? defaults.toDate,
    page: options.page ?? 1,
    pageSize: options.pageSize ?? 20,
  });

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: unknown;
        pageSize?: unknown;
        totalRecords?: unknown;
        totalPages?: unknown;
      };
    }>
  >(API_ENDPOINTS.LEAVE.HISTORY, {
    params: {
      fromDate: normalized.filters.fromDate,
      toDate: normalized.filters.toDate,
      page: normalized.page,
      pageSize: normalized.pageSize,
    },
  });

  const data = response.data.data ?? {};
  const pagination = data.pagination ?? {};
  const items = Array.isArray(data.items)
    ? data.items
        .map(normalizeApplication)
        .filter((item): item is LeaveApplicationListItem => item !== null)
    : [];
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const pageSize = numberOrNull(pagination.pageSize) ?? normalized.pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / Math.max(pageSize, 1)));

  return {
    items,
    page: numberOrNull(pagination.page) ?? normalized.page,
    pageSize,
    totalItems,
    totalPages,
  };
}

export async function getLeaveApplicationDetails(
  leaveApplicationId: number,
): Promise<LeaveApplicationDetails> {
  if (!Number.isInteger(leaveApplicationId) || leaveApplicationId <= 0) {
    throw Object.assign(
      new Error('leaveApplicationId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }

  const response = await apiClient.get<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.LEAVE.REQUEST_DETAILS(leaveApplicationId));

  const payload = response.data.data ?? {};
  const row =
    payload &&
    typeof payload === 'object' &&
    'request' in payload &&
    payload.request &&
    typeof payload.request === 'object'
      ? (payload.request as ObjectValue)
      : (payload as ObjectValue);

  const details = normalizeDetails(row);
  if (!details) {
    throw Object.assign(new Error('Leave application was not found.'), {
      code: 'LEAVE_APPLICATION_NOT_FOUND',
    });
  }
  return details;
}

export async function applyLeave(
  input: ApplyLeaveInput,
): Promise<LeaveApplicationDetails> {
  const leaveTypeId = Number(input.leaveTypeId);
  const leavePolicyId = Number(input.leavePolicyId);
  if (!Number.isInteger(leaveTypeId) || leaveTypeId <= 0) {
    throw Object.assign(new Error('leaveTypeId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  if (!Number.isInteger(leavePolicyId) || leavePolicyId <= 0) {
    throw Object.assign(new Error('leavePolicyId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  const reason = textOrNull(input.reason);
  if (!reason) {
    throw Object.assign(new Error('reason is required'), {
      code: 'VALIDATION_ERROR',
    });
  }
  const sessionType = normalizeSessionCode(input.sessionType);
  if (!sessionType) {
    throw Object.assign(new Error('sessionType is required'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const body: Record<string, unknown> = {
    leaveTypeId,
    leavePolicyId,
    fromDate: input.fromDate,
    toDate: input.toDate,
    sessionType,
    reason,
    emergencyContact: textOrNull(input.emergencyContact) ?? undefined,
    handoverNotes: textOrNull(input.handoverNotes) ?? undefined,
  };
  if (input.totalDays != null && Number.isFinite(Number(input.totalDays))) {
    body.totalDays = Number(input.totalDays);
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.LEAVE.APPLY, body);

  const payload = response.data.data ?? {};
  const row =
    payload &&
    typeof payload === 'object' &&
    'request' in payload &&
    payload.request &&
    typeof payload.request === 'object'
      ? (payload.request as ObjectValue)
      : (payload as ObjectValue);

  const details = normalizeDetails(row);
  if (!details) {
    throw Object.assign(new Error('Leave application could not be created.'), {
      code: 'LEAVE_APPLY_FAILED',
    });
  }
  return details;
}

export async function cancelLeaveApplication(
  leaveApplicationId: number,
  reason: string,
): Promise<LeaveApplicationDetails> {
  if (!Number.isInteger(leaveApplicationId) || leaveApplicationId <= 0) {
    throw Object.assign(
      new Error('leaveApplicationId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }
  const cancellationReason = textOrNull(reason);
  if (!cancellationReason) {
    throw Object.assign(new Error('cancellationReason is required'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.LEAVE.CANCEL(leaveApplicationId), {
    cancellationReason,
  });

  const payload = response.data.data ?? {};
  const row =
    payload &&
    typeof payload === 'object' &&
    'request' in payload &&
    payload.request &&
    typeof payload.request === 'object'
      ? (payload.request as ObjectValue)
      : (payload as ObjectValue);

  const details = normalizeDetails(row);
  if (!details) {
    throw Object.assign(new Error('Leave application was not found.'), {
      code: 'LEAVE_APPLICATION_NOT_FOUND',
    });
  }
  return details;
}

export async function approveLeaveApplication(
  leaveApplicationId: number,
): Promise<LeaveApplicationDetails> {
  if (!Number.isInteger(leaveApplicationId) || leaveApplicationId <= 0) {
    throw Object.assign(
      new Error('leaveApplicationId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.LEAVE.APPROVE(leaveApplicationId));

  const payload = response.data.data ?? {};
  const row =
    payload &&
    typeof payload === 'object' &&
    'request' in payload &&
    payload.request &&
    typeof payload.request === 'object'
      ? (payload.request as ObjectValue)
      : (payload as ObjectValue);

  const details = normalizeDetails(row);
  if (!details) {
    throw Object.assign(new Error('Leave application was not found.'), {
      code: 'LEAVE_APPLICATION_NOT_FOUND',
    });
  }
  return details;
}

export async function rejectLeaveApplication(
  leaveApplicationId: number,
  reason: string,
): Promise<LeaveApplicationDetails> {
  if (!Number.isInteger(leaveApplicationId) || leaveApplicationId <= 0) {
    throw Object.assign(
      new Error('leaveApplicationId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }
  const rejectionReason = textOrNull(reason);
  if (!rejectionReason) {
    throw Object.assign(new Error('rejectionReason is required'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.LEAVE.REJECT(leaveApplicationId), {
    rejectionReason,
  });

  const payload = response.data.data ?? {};
  const row =
    payload &&
    typeof payload === 'object' &&
    'request' in payload &&
    payload.request &&
    typeof payload.request === 'object'
      ? (payload.request as ObjectValue)
      : (payload as ObjectValue);

  const details = normalizeDetails(row);
  if (!details) {
    throw Object.assign(new Error('Leave application was not found.'), {
      code: 'LEAVE_APPLICATION_NOT_FOUND',
    });
  }
  return details;
}
